import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { JuryShell } from './JuryDashboard';
import { formatApiErrorDetail } from '@/context/JuryAuthContext';
import { ArrowLeft, FileText, ExternalLink, Save, Check, AlertTriangle, Film, FolderOpen, Info, Pencil } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ax = axios.create({ withCredentials: true });

/**
 * Return an openable URL for an uploaded asset. Uploads live in one of two
 * places: (a) Cloudflare R2 — absolute URL, (b) local disk fallback —
 * relative path like `/api/uploads/<hash>.pdf`.
 */
const resolveAssetUrl = (raw) => {
  if (!raw) return '';
  const s = String(raw).trim();
  if (/^(https?:)?\/\//i.test(s)) return s;
  return `${BACKEND_URL}${s.startsWith('/') ? '' : '/'}${s}`;
};

const resolveExternalLink = (raw) => {
  if (!raw) return '';
  const s = String(raw).trim();
  if (/^(https?:)?\/\//i.test(s)) return s;
  return `https://${s}`;
};

const ENTRY_FIELDS = [
  { key: 'campaign_summary', label: 'Campaign Summary' },
  { key: 'business_challenge', label: 'Business Challenge' },
  { key: 'strategy_idea', label: 'Strategy & Creative Idea' },
  { key: 'execution', label: 'Execution & Implementation' },
  { key: 'campaign_objectives', label: 'Campaign Objectives' },
  { key: 'business_impact', label: 'Business & Brand Impact' },
];

// Sub-categories to display on the scoring aside — ONLY the ones the entrant
// actually opted into (primary `category` + `additional_categories`), never
// the full pillar list. Stored format is "Pillar Name - Sub Category Name" so
// we split on " - " and take the second half. De-duped, order preserved.
const _splitSubCategory = (raw) => {
  const s = String(raw || '').trim();
  if (!s) return '';
  const parts = s.split(/\s*[-–—]\s*/); // en/em-dash tolerant
  return (parts.length > 1 ? parts.slice(1).join(' - ') : parts[0]).trim();
};
const getEntrantSubCategories = (entry) => {
  if (!entry) return [];
  const raw = [entry.category, ...(Array.isArray(entry.additional_categories) ? entry.additional_categories : [])];
  const seen = new Set();
  const out = [];
  for (const r of raw) {
    const sub = _splitSubCategory(r);
    if (!sub || seen.has(sub.toLowerCase())) continue;
    seen.add(sub.toLowerCase());
    out.push(sub);
  }
  return out;
};

const JuryEntryReview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [entry, setEntry] = useState(null);
  const [scoreDoc, setScoreDoc] = useState(null);
  const [framework, setFramework] = useState(null);
  const [principles, setPrinciples] = useState([]);
  const [criterionScores, setCriterionScores] = useState({});
  const [comments, setComments] = useState('');
  const [strengths, setStrengths] = useState('');
  const [conflict, setConflict] = useState(null); // null | false | true
  const [conflictConfirm, setConflictConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showPrinciples, setShowPrinciples] = useState(true);
  const [editing, setEditing] = useState(false);

  const hydrateFromScore = (score, fw) => {
    const cs = {};
    ((fw && fw.criteria) || []).forEach((c) => { cs[c.key] = 0; });
    if (score) {
      if (score.criterion_scores && Object.keys(score.criterion_scores).length) {
        Object.assign(cs, score.criterion_scores);
      } else if (score.scores) {
        Object.entries(score.scores).forEach(([k, v]) => { if (k in cs) cs[k] = v; });
      }
      setComments(score.comments || '');
      setStrengths(score.strengths || '');
      setConflict(score.conflict ? true : false);
    }
    setCriterionScores(cs);
  };

  const resetToSaved = () => {
    hydrateFromScore(scoreDoc, framework);
    setEditing(false);
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await ax.get(`${API}/jury/entries/${id}`);
        setEntry(data.entry);
        setScoreDoc(data.score);
        setFramework(data.framework || null);
        setPrinciples(data.principles || []);
        hydrateFromScore(data.score, data.framework || null);
      } catch (err) {
        const d = err.response?.data?.detail;
        toast.error(typeof d === 'string' ? d : 'Could not open entry.');
        navigate('/jury/entries');
      } finally { setLoading(false); }
    })();
  }, [id, navigate]);

  const submitted = scoreDoc?.status === 'submitted';
  const locked = submitted && !editing;

  // Auto-calculate weighted total: (score/10) × weight per criterion.
  const { breakdown, total } = useMemo(() => {
    if (!framework) return { breakdown: [], total: 0 };
    let running = 0;
    const rows = (framework.criteria || []).map((c) => {
      const s = Math.max(0, Math.min(10, parseInt(criterionScores[c.key] || 0, 10) || 0));
      const weighted = (s / 10) * (c.weight || 0);
      running += weighted;
      return { ...c, score: s, weighted: Math.round(weighted * 100) / 100 };
    });
    return { breakdown: rows, total: Math.round(running * 100) / 100 };
  }, [framework, criterionScores]);

  const save = async (submitFlag) => {
    if (submitFlag && !comments.trim()) {
      toast.error('Overall Comments are mandatory before submitting.');
      return;
    }
    if (submitFlag && framework) {
      const missing = (framework.criteria || []).filter((c) => !(parseInt(criterionScores[c.key] || 0, 10) > 0));
      if (missing.length) {
        toast.error(`Please score every criterion: ${missing.map((m) => m.label).join(', ')}`);
        return;
      }
    }
    setBusy(true);
    try {
      await ax.post(`${API}/jury/scores`, {
        nomination_id: id,
        criterion_scores: criterionScores,
        comments,
        strengths,
        conflict: false,
        submit: submitFlag,
      });
      toast.success(submitFlag ? (submitted ? 'Evaluation updated.' : 'Evaluation submitted.') : 'Draft saved.');
      if (submitFlag) navigate('/jury-chamber/entries');
      else {
        const { data } = await ax.get(`${API}/jury/scores/${id}`);
        setScoreDoc(data);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Save failed.');
    } finally { setBusy(false); }
  };

  const declareConflict = async () => {
    setBusy(true);
    try {
      await ax.post(`${API}/jury/scores`, {
        nomination_id: id,
        comments: comments || 'Conflict declared by juror.',
        conflict: true,
        submit: false,
      });
      toast.success('Conflict noted. Entry removed from your queue.');
      navigate('/jury-chamber/entries');
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Could not record conflict.');
    } finally { setBusy(false); setConflictConfirm(false); }
  };

  if (loading) {
    return <JuryShell active="entries"><div className="p-12 text-[var(--ink-muted)] tracking-[0.3em] uppercase text-xs">Loading entry…</div></JuryShell>;
  }
  if (!entry) return null;

  return (
    <JuryShell active="entries">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8" data-testid="jury-review-page">
        <div className="flex items-center justify-between mb-6">
          <Link to="/jury-chamber/entries" data-testid="jury-review-back" className="inline-flex items-center gap-2 text-[var(--ink-muted)] hover:text-[var(--gold)] text-[10px] tracking-[0.3em] uppercase transition-colors">
            <ArrowLeft size={14} /> Back to entries
          </Link>
          {submitted && (
            <span data-testid="jury-review-submitted-badge" className={`inline-flex items-center gap-1 border text-[10px] tracking-[0.3em] uppercase px-3 py-2 ${editing ? 'border-[var(--gold)]/50 text-[var(--gold)]' : 'border-[#7BC47F]/40 text-[#7BC47F]'}`}>
              {editing ? <><Pencil size={12} /> Editing Evaluation</> : <><Check size={12} /> Evaluation Submitted</>}
            </span>
          )}
        </div>

        {/* General Judging Principles banner */}
        {principles.length > 0 && (
          <div
            data-testid="jury-principles-banner"
            className="mb-8 border border-[var(--gold-faint)] bg-[var(--bg-alt)]/40 p-5"
          >
            <button
              type="button"
              onClick={() => setShowPrinciples((v) => !v)}
              className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[var(--gold)] hover:text-[var(--gold-bright)] transition-colors"
              data-testid="jury-principles-toggle"
            >
              <Info size={12} />
              General Judging Principles
              <span className="text-[var(--ink-muted)] ml-2">{showPrinciples ? '(hide)' : '(show)'}</span>
            </button>
            {showPrinciples && (
              <ul className="mt-4 space-y-2 text-[var(--ink)] text-[13px] leading-relaxed">
                {principles.map((p, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="numeral text-[var(--gold)] text-xs mt-0.5">{String(i + 1).padStart(2, '0')}</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.15fr] gap-10">
          {/* LEFT: Entry */}
          <div data-testid="jury-review-left" className="space-y-8">
            <div>
              <div className="text-[10px] tracking-[0.4em] uppercase text-[var(--gold)] mb-2">{entry.category}</div>
              <h1 className="font-[Libre_Baskerville] text-4xl text-[var(--ink)] leading-[1.1]">{entry.entry_title || 'Untitled entry'}</h1>
              <div className="mt-4 flex flex-wrap gap-x-8 gap-y-2 text-sm text-[var(--ink-muted)]">
                {entry.brand_name && <span>Organisation: <span className="text-[var(--ink)]/85">{entry.brand_name}</span></span>}
                {(entry.campaign_start || entry.campaign_end) && (
                  <span>Duration: <span className="text-[var(--ink)]/85">{entry.campaign_start || '—'} → {entry.campaign_end || '—'}</span></span>
                )}
                {entry.primary_market && <span>Market: <span className="text-[var(--ink)]/85">{entry.primary_market}</span></span>}
              </div>
            </div>

            {/* Long sections — section headers use editorial serif so they
                stand out clearly from the body copy (jury feedback: the tiny
                tracked eyebrows were too easy to skim past). */}
            {ENTRY_FIELDS.map(({ key, label }) => entry[key] && (
              <section key={key}>
                <h2 className="font-[Libre_Baskerville] text-2xl md:text-[26px] text-[var(--ink)] mb-3 leading-tight" data-testid={`section-header-${key}`}>{label}</h2>
                <p className="text-[var(--ink)] text-[15px] leading-[1.75] whitespace-pre-wrap">{entry[key]}</p>
              </section>
            ))}

            {Array.isArray(entry.key_results) && entry.key_results.filter(Boolean).length > 0 && (
              <section>
                <h2 className="font-[Libre_Baskerville] text-2xl md:text-[26px] text-[var(--ink)] mb-4 leading-tight" data-testid="section-header-key-results">Key Results</h2>
                <ul className="space-y-2">
                  {entry.key_results.filter(Boolean).map((k, i) => (
                    <li key={i} className="flex items-start gap-3 text-[var(--ink)] text-[15px] leading-relaxed">
                      <span className="numeral text-[var(--gold)] text-sm mt-1">{String(i + 1).padStart(2, '0')}</span>
                      <span>{k}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <section>
              <h2 className="font-[Libre_Baskerville] text-2xl md:text-[26px] text-[var(--ink)] mb-4 leading-tight" data-testid="section-header-supporting">Supporting Material</h2>
              <div className="space-y-2">
                {entry.case_study_deck_url && (
                  <a data-testid="jury-link-deck" href={resolveAssetUrl(entry.case_study_deck_url)} target="_blank" rel="noreferrer" className="flex items-center gap-3 border border-[var(--gold-faint)] hover:border-[var(--gold)] px-4 py-3 text-[var(--ink)] hover:text-[var(--gold)] transition-colors">
                    <FileText size={14} className="text-[var(--gold)]" /> <span className="flex-1 truncate text-sm">{entry.case_study_deck_name || 'Case Study Deck'}</span> <ExternalLink size={12} />
                  </a>
                )}
                {entry.case_study_deck_url_2 && (
                  <a data-testid="jury-link-deck-2" href={resolveAssetUrl(entry.case_study_deck_url_2)} target="_blank" rel="noreferrer" className="flex items-center gap-3 border border-[var(--gold-faint)] hover:border-[var(--gold)] px-4 py-3 text-[var(--ink)] hover:text-[var(--gold)] transition-colors">
                    <FileText size={14} className="text-[var(--gold)]" /> <span className="flex-1 truncate text-sm">{entry.case_study_deck_name_2 || 'Supplementary Deck 1'}</span> <ExternalLink size={12} />
                  </a>
                )}
                {entry.case_study_deck_url_3 && (
                  <a data-testid="jury-link-deck-3" href={resolveAssetUrl(entry.case_study_deck_url_3)} target="_blank" rel="noreferrer" className="flex items-center gap-3 border border-[var(--gold-faint)] hover:border-[var(--gold)] px-4 py-3 text-[var(--ink)] hover:text-[var(--gold)] transition-colors">
                    <FileText size={14} className="text-[var(--gold)]" /> <span className="flex-1 truncate text-sm">{entry.case_study_deck_name_3 || 'Supplementary Deck 2'}</span> <ExternalLink size={12} />
                  </a>
                )}
                {entry.video_link && (
                  <a data-testid="jury-link-video" href={resolveExternalLink(entry.video_link)} target="_blank" rel="noreferrer" className="flex items-center gap-3 border border-[var(--gold-faint)] hover:border-[var(--gold)] px-4 py-3 text-[var(--ink)] hover:text-[var(--gold)] transition-colors">
                    <Film size={14} className="text-[var(--gold)]" /> <span className="flex-1 truncate text-sm">{entry.video_link}</span> <ExternalLink size={12} />
                  </a>
                )}
                {entry.supporting_material_link && (
                  <a data-testid="jury-link-assets" href={resolveExternalLink(entry.supporting_material_link)} target="_blank" rel="noreferrer" className="flex items-center gap-3 border border-[var(--gold-faint)] hover:border-[var(--gold)] px-4 py-3 text-[var(--ink)] hover:text-[var(--gold)] transition-colors">
                    <FolderOpen size={14} className="text-[var(--gold)]" /> <span className="flex-1 truncate text-sm">{entry.supporting_material_link}</span> <ExternalLink size={12} />
                  </a>
                )}
                {!(entry.case_study_deck_url || entry.case_study_deck_url_2 || entry.case_study_deck_url_3 || entry.video_link || entry.supporting_material_link) && (
                  <p className="text-[var(--ink-muted)] text-sm italic">No supporting material attached.</p>
                )}
              </div>
            </section>

            {(entry.credits_brand || entry.credits_creative || entry.credits_production) && (
              <section>
                <h2 className="font-[Libre_Baskerville] text-2xl md:text-[26px] text-[var(--ink)] mb-4 leading-tight" data-testid="section-header-credits">Credits</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--gold-faint)]/25">
                  {entry.credits_brand && <Credit label="Organisation" value={entry.credits_brand} />}
                  {entry.credits_creative && <Credit label="Agency" value={entry.credits_creative} />}
                  {entry.credits_production && <Credit label="Production" value={entry.credits_production} />}
                </div>
              </section>
            )}
          </div>

          {/* RIGHT: Scoring — scrolls with the page (no sticky) so jurors
              read the campaign narrative and score in a single flow rather
              than two independently scrolling columns. */}
          <aside data-testid="jury-review-right" className="self-start space-y-6 bg-[var(--bg-alt)]/40 border border-[var(--gold-faint)] p-6 md:p-8">
            <div>
              <div className="text-[10px] tracking-[0.4em] uppercase text-[var(--gold)] mb-1">Scoring Framework</div>
              <div className="font-[Libre_Baskerville] text-xl text-[var(--ink)]" data-testid="jury-framework-name">{framework?.name || 'General Excellence'}</div>
              {(() => {
                const subs = getEntrantSubCategories(entry);
                if (!subs.length) return null;
                return (
                  <div className="mt-4 border-t border-[var(--gold-faint)] pt-3" data-testid="jury-subcategories">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--ink-muted)] mb-2">Sub-Categories Entered</div>
                    <ul className="space-y-1.5">
                      {subs.map((s) => (
                        <li key={s} className="flex items-start gap-2 text-[var(--ink)]/85 text-[12.5px] leading-snug">
                          <span className="text-[var(--gold)] mt-[3px] shrink-0">•</span>
                          <span>{s}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })()}
              <p className="text-[var(--ink-muted)] text-xs mt-4">Score each parameter 1–10. Weighted total is calculated automatically.</p>
            </div>

            <fieldset disabled={locked} className="space-y-5" data-testid="jury-score-fieldset">
              {(framework?.criteria || []).map((c) => (
                <ScoreRow
                  key={c.key}
                  criterion={c}
                  value={criterionScores[c.key] || 0}
                  weighted={breakdown.find((b) => b.key === c.key)?.weighted || 0}
                  onChange={(v) => setCriterionScores((s) => ({ ...s, [c.key]: v }))}
                />
              ))}

              <div className="border-t border-[var(--gold-faint)] pt-4 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--ink-muted)]">Weighted Total</div>
                  <div className="font-[Libre_Baskerville] text-3xl text-[var(--gold)]" data-testid="score-total">
                    {total}<span className="text-[var(--ink-muted)] text-base"> / 100</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--ink-muted)]">Overall Percentage</div>
                  <div className="text-[var(--gold)] text-sm numeral" data-testid="score-percentage">{total.toFixed(2)}%</div>
                </div>
              </div>

              <div>
                <label className="block text-[10px] tracking-[0.3em] uppercase text-[var(--ink-muted)] mb-2">
                  Overall Comments <span className="text-[var(--terracotta)]">*</span>
                </label>
                <textarea data-testid="score-comments" value={comments} onChange={(e) => setComments(e.target.value)} rows={4} placeholder="Mandatory juror notes — overall verdict on this entry." className="w-full bg-[var(--bg)] border border-[var(--gold-faint)] focus:border-[var(--gold)] outline-none p-3 text-[var(--ink)] text-sm resize-y" />
              </div>

              <div>
                <label className="block text-[10px] tracking-[0.3em] uppercase text-[var(--ink-muted)] mb-2">
                  Strengths <span className="text-[var(--ink-muted)] normal-case tracking-normal">(optional)</span>
                </label>
                <textarea data-testid="score-strengths" value={strengths} onChange={(e) => setStrengths(e.target.value)} rows={3} placeholder="What did the entry do particularly well?" className="w-full bg-[var(--bg)] border border-[var(--gold-faint)] focus:border-[var(--gold)] outline-none p-3 text-[var(--ink)] text-sm resize-y" />
              </div>

              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--ink-muted)] mb-2">Conflict Declaration</div>
                <div className="flex gap-3 text-sm">
                  <button type="button" onClick={() => setConflict(false)} data-testid="conflict-none" className={`flex-1 border px-3 py-2 transition-colors ${conflict === false ? 'border-[var(--gold)] text-[var(--gold)]' : 'border-[var(--gold-faint)] text-[var(--ink-muted)] hover:border-[var(--gold-soft)]'}`}>No Conflict</button>
                  <button type="button" onClick={() => setConflictConfirm(true)} data-testid="conflict-exists" className={`flex-1 border px-3 py-2 transition-colors ${conflict === true ? 'border-[var(--terracotta)] text-[var(--terracotta)]' : 'border-[var(--gold-faint)] text-[var(--ink-muted)] hover:border-[var(--terracotta-soft)]'}`}>Conflict Exists</button>
                </div>
              </div>

              {!submitted && (
                <div className="space-y-3 pt-2">
                  <button type="button" onClick={() => save(false)} disabled={busy} data-testid="score-save-draft" className="w-full inline-flex items-center justify-center gap-2 border border-[var(--gold-faint)] hover:border-[var(--gold)] hover:text-[var(--gold)] text-[var(--ink-muted)] text-[10px] tracking-[0.3em] uppercase py-3 transition-colors disabled:opacity-50">
                    <Save size={12} /> {busy ? 'Saving…' : 'Save Draft'}
                  </button>
                  <button type="button" onClick={() => save(true)} disabled={busy} data-testid="score-submit" className="w-full inline-flex items-center justify-center gap-2 bg-[var(--gold)] hover:bg-[var(--gold-bright)] text-[var(--bg)] text-[10px] tracking-[0.3em] uppercase py-3 transition-colors disabled:opacity-50">
                    {busy ? 'Submitting…' : 'Submit Final Evaluation'}
                  </button>
                </div>
              )}
              {submitted && editing && (
                <div className="space-y-3 pt-2">
                  <button type="button" onClick={() => save(true)} disabled={busy} data-testid="score-update" className="w-full inline-flex items-center justify-center gap-2 bg-[var(--gold)] hover:bg-[var(--gold-bright)] text-[var(--bg)] text-[10px] tracking-[0.3em] uppercase py-3 transition-colors disabled:opacity-50">
                    {busy ? 'Updating…' : 'Update Evaluation'}
                  </button>
                  <button type="button" onClick={resetToSaved} disabled={busy} data-testid="score-cancel-edit" className="w-full inline-flex items-center justify-center gap-2 border border-[var(--gold-faint)] hover:border-[var(--gold)] hover:text-[var(--gold)] text-[var(--ink-muted)] text-[10px] tracking-[0.3em] uppercase py-3 transition-colors disabled:opacity-50">
                    Cancel
                  </button>
                </div>
              )}
            </fieldset>
            {locked && (
              <div className="mt-5 space-y-3">
                <div className="border border-[#7BC47F]/40 bg-[#7BC47F]/5 p-4 text-center text-[#7BC47F] text-[10px] tracking-[0.3em] uppercase" data-testid="score-locked-note">
                  Evaluation submitted{scoreDoc?.edited_at ? ' · edited' : ''}
                </div>
                <button type="button" onClick={() => setEditing(true)} data-testid="score-edit" className="w-full inline-flex items-center justify-center gap-2 border border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-[var(--bg)] text-[10px] tracking-[0.3em] uppercase py-3 transition-colors">
                  <Pencil size={12} /> Edit Evaluation
                </button>
              </div>
            )}
          </aside>
        </div>
      </div>

      {conflictConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="conflict-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setConflictConfirm(false)} />
          <div className="relative bg-[var(--bg-alt)] border border-[var(--terracotta-soft)] max-w-md w-full p-8">
            <div className="flex items-center gap-3 mb-3">
              <AlertTriangle className="text-[var(--terracotta)]" size={20} />
              <h3 className="font-[Libre_Baskerville] text-xl text-[var(--ink)]">Declare conflict of interest?</h3>
            </div>
            <p className="text-[var(--ink-muted)] text-sm">
              This entry will be removed from your evaluation queue and your scores will not be recorded for it.
              This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setConflictConfirm(false)} className="text-[var(--ink-muted)] text-xs tracking-[0.2em] uppercase px-4 py-2">Cancel</button>
              <button onClick={declareConflict} disabled={busy} data-testid="conflict-confirm" className="bg-[var(--terracotta)] hover:opacity-90 text-white text-xs tracking-[0.2em] uppercase px-6 py-2 disabled:opacity-50">
                {busy ? 'Recording…' : 'Confirm conflict'}
              </button>
            </div>
          </div>
        </div>
      )}
    </JuryShell>
  );
};

const Credit = ({ label, value }) => (
  <div className="bg-[var(--bg)] p-4">
    <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--ink-muted)] mb-1">{label}</div>
    <div className="text-[var(--ink)] text-sm">{value}</div>
  </div>
);

const ScoreRow = ({ criterion, value, weighted, onChange }) => (
  <div data-testid={`field-score-${criterion.key}`}>
    <div className="flex items-start justify-between mb-1 gap-3">
      <label className="text-[var(--ink)] text-sm leading-tight">
        {criterion.label}
        <span className="ml-2 text-[10px] tracking-[0.2em] uppercase text-[var(--gold)]">{criterion.weight}%</span>
      </label>
      <div className="text-right shrink-0">
        <span className="font-[Libre_Baskerville] text-xl text-[var(--gold)] tabular-nums" data-testid={`score-${criterion.key}-value`}>
          {value}<span className="text-[var(--ink-muted)] text-xs">/10</span>
        </span>
        <div className="text-[10px] text-[var(--ink-muted)] mt-0.5" data-testid={`score-${criterion.key}-weighted`}>
          weighted {weighted.toFixed(1)}
        </div>
      </div>
    </div>
    <input
      type="range"
      min="0"
      max="10"
      step="1"
      value={value || 0}
      onChange={(e) => onChange(parseInt(e.target.value))}
      data-testid={`score-${criterion.key}`}
      className="w-full accent-[var(--gold)]"
    />
    <div className="flex justify-between text-[10px] text-[var(--ink-muted)] mt-0.5 px-0.5">
      <span>0</span><span>5</span><span>10</span>
    </div>
  </div>
);

export default JuryEntryReview;
